import { AssetIcon } from 'assets/icons/AssetIcon';
import cx from 'classnames';
import Button from 'components/button/Button';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import { START_NUMBER_DEFAULT } from 'constants/photo.const';
import { useModal } from 'hooks/useModal';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import { useTranslation } from 'hooks/useTranslation';
import { chain, filter, map, orderBy, size, times } from 'lodash';
import { CustomerFeedbackModel } from 'models/printing-photo/customer-feedback.model';
import React, { HtmlHTMLAttributes, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateCustomerFeedbackApiActionMutation } from 'store/api/feedback.slice-api';
import { useAppSelector } from 'store/store-hooks';
import FeedbackPopup from './FeedbackPopup';

export type FeedbackTableProps = HtmlHTMLAttributes<HTMLDivElement> & {
  isVerticalScreen: boolean;
};

const FeedbackTable: React.FC<FeedbackTableProps> = ({
  isVerticalScreen,
  ...props
}) => {
  const { T } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const [createCustomerFeedbackApiAction] =
    useCreateCustomerFeedbackApiActionMutation();
  const { satisfactionLevel, transactionId } = useAppSelector(
    (state) => state.selfeApp,
  );
  const { listFeedbackCriteria } = useAppSelector(
    (state) => state.feedbackCriteria,
  );

  const [ratings, setRatings] = useState<{ [criteriaId: string]: number }>(() =>
    chain(listFeedbackCriteria?.data)
      .keyBy('id')
      .mapValues(() => START_NUMBER_DEFAULT - satisfactionLevel + 1)
      .value(),
  );

  const [selectedAnswers, setSelectedAnswers] = useState<{
    [criteriaId: string]: number;
  }>(() =>
    chain(listFeedbackCriteria?.data)
      .filter((item) => item.type === 2)
      .filter((item) => item.Answers && size(item.Answers) > 0)
      .keyBy('id')
      .mapValues((item) => item.Answers[0].id)
      .value(),
  );

  const { getNextPath } = useNewPhotoLifeCycleStep();
  const { openConfirmModal } = useModal();
  const navigate = useNavigate();

  const handleStartClick = (starIndex: number, criteriaId: number) => {
    const newRating = starIndex + 1;
    setRatings((prev) => ({
      ...prev,
      [criteriaId]: newRating,
    }));
  };

  const handleAnswerClick = (answerId: number, criteriaId: number) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [criteriaId]: answerId,
    }));
  };

  const handleRedirectToStart = useCallback(() => {
    navigate(getNextPath() || '');
  }, [getNextPath, navigate]);

  const getFeedbackData = useCallback((): CustomerFeedbackModel => {
    const feedbackEvaluationCriterias = map(
      listFeedbackCriteria?.data,
      (criteria) => ({
        FeedbackEvaluationCriteriaId: criteria?.id,
        FeedbackEvaluationCriteriaAnswerId: selectedAnswers[criteria?.id],
        StarNumber: ratings[criteria?.id],
      }),
    );

    return {
      FeedbackEvaluationCriterias: feedbackEvaluationCriterias,
      TransactionId: transactionId,
      SatisfactionLevel: satisfactionLevel,
    };
  }, [
    listFeedbackCriteria?.data,
    ratings,
    satisfactionLevel,
    selectedAnswers,
    transactionId,
  ]);

  const handleSendFeedback = useCallback(() => {
    const feedbackData = getFeedbackData();
    createCustomerFeedbackApiAction(feedbackData);
    openConfirmModal({
      RenderContent: () => <FeedbackPopup />,
      hidePrev: true,
      onOk: handleRedirectToStart,
    });
  }, [
    createCustomerFeedbackApiAction,
    getFeedbackData,
    handleRedirectToStart,
    openConfirmModal,
  ]);

  usePageTimer({
    forwardFunc: () => handleRedirectToStart(),
    isNotShowConfirm: true,
  });

  return (
    <>
      <div className="feedback-table">
        {map(
          orderBy(
            filter(listFeedbackCriteria?.data, (item) => item?.type === 1),
            ['orderNo'],
            ['asc'],
          ),
          (item, index) => (
            <div key={item?.id} className="criterial-item">
              <Typography
                className="criterial-label"
                variant={TYPOGRAPHY_VARIANTS.H2}
              >
                {item?.name}
              </Typography>
              <div className="star-list">
                {times(START_NUMBER_DEFAULT, (starIndex) => (
                  <AssetIcon.StarIcon
                    key={starIndex}
                    className={cx('star', {
                      active: ratings[item?.id] > starIndex,
                    })}
                    onClick={() => handleStartClick(starIndex, item?.id)}
                  />
                ))}
              </div>
            </div>
          ),
        )}
        {map(
          orderBy(
            filter(listFeedbackCriteria?.data, (item) => item?.type === 2),
            ['orderNo'],
            ['asc'],
          ),
          (item) => (
            <div key={item?.id} className="group-people">
              <Typography
                className="group-people-label"
                variant={TYPOGRAPHY_VARIANTS.H2}
              >
                {item?.name}
              </Typography>
              <div className="group-people-list">
                {map(item?.Answers, (answer) => (
                  <Typography
                    className={`group-people-item-label ${
                      selectedAnswers[item?.id] === answer?.id ? 'selected' : ''
                    }`}
                    variant={TYPOGRAPHY_VARIANTS.BODY}
                    onClick={() => handleAnswerClick(answer?.id, item?.id)}
                    key={answer?.id}
                  >
                    {answer?.name}
                  </Typography>
                ))}
              </div>
            </div>
          ),
        )}
      </div>
      <div className="feedback-action">
        <Button onClick={handleSendFeedback}>{T('send')}</Button>
      </div>
    </>
  );
};

export default memo(FeedbackTable);
